import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { Plus, RefreshCw, CheckCircle2, XCircle, Loader2, DollarSign, Clock, Banknote } from "lucide-react";

const STATUS_META = {
  pending_confirmation: { label: "Pending Result", color: "bg-gray-100 text-gray-700" },
  confirmed_success: { label: "Confirmed Successful", color: "bg-emerald-100 text-emerald-700" },
  confirmed_failed: { label: "Confirmed Failed", color: "bg-red-100 text-red-700" },
  paid_out: { label: "Paid Out", color: "bg-blue-100 text-blue-700" },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-500" },
};

const BrokerDashboard = () => {
  const { user } = useAuth();
  const isAccountsOrAdmin = user?.role === "accounts" || user?.role === "admin";
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resultSubmittingId, setResultSubmittingId] = useState(null);
  const [payoutDialog, setPayoutDialog] = useState(null);
  const [paymentRef, setPaymentRef] = useState("");
  const [disputeDialog, setDisputeDialog] = useState(null);
  const [disputeOutcome, setDisputeOutcome] = useState("broker_wins");
  const [disputeReason, setDisputeReason] = useState("");
  const [resolvingDispute, setResolvingDispute] = useState(false);
  
  const [formData, setFormData] = useState({
    tenant_name: "",
    tenant_email: "",
    tenant_phone: "",
    tenant_payment_method: "bank",  // 'bank' or 'mobile_money'
    tenant_bank_name: "",
    tenant_account_number: "",
    tenant_mobile_provider: "MTN",  // MTN | Airtel | Zamtel
    tenant_mobile_number: "",
    deposit_amount: "",
    description: "",
    property_reference: "",
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/broker/transactions`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.tenant_name || !formData.deposit_amount) {
      toast.error("Tenant name and deposit amount are required");
      return;
    }
    // Validate payment method details
    if (formData.tenant_payment_method === "bank") {
      if (!formData.tenant_bank_name || !formData.tenant_account_number) {
        toast.error("Bank name and account number are required for bank payouts");
        return;
      }
    } else if (formData.tenant_payment_method === "mobile_money") {
      if (!formData.tenant_mobile_provider || !formData.tenant_mobile_number) {
        toast.error("Mobile provider and mobile number are required");
        return;
      }
    }
    setSubmitting(true);
    try {
      // Only send the fields relevant to the chosen payment method (cleaner DB records)
      const payload = {
        tenant_name: formData.tenant_name,
        tenant_email: formData.tenant_email || null,
        tenant_phone: formData.tenant_phone || null,
        tenant_payment_method: formData.tenant_payment_method,
        tenant_bank_name: formData.tenant_payment_method === "bank" ? formData.tenant_bank_name : null,
        tenant_account_number: formData.tenant_payment_method === "bank" ? formData.tenant_account_number : null,
        tenant_mobile_provider: formData.tenant_payment_method === "mobile_money" ? formData.tenant_mobile_provider : null,
        tenant_mobile_number: formData.tenant_payment_method === "mobile_money" ? formData.tenant_mobile_number : null,
        deposit_amount: parseFloat(formData.deposit_amount),
        description: formData.description || null,
        property_reference: formData.property_reference || null,
      };
      const res = await axios.post(`${API}/broker/transactions`, payload);
      toast.success(`Recorded. Commission: K ${res.data.commission_amount}, Payout: K ${res.data.payout_amount}`);
      setShowForm(false);
      setFormData({ tenant_name: "", tenant_email: "", tenant_phone: "", tenant_payment_method: "bank", tenant_bank_name: "", tenant_account_number: "", tenant_mobile_provider: "MTN", tenant_mobile_number: "", deposit_amount: "", description: "", property_reference: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  const submitResult = async (tx, result) => {
    if (!window.confirm(`Mark this transaction as ${result.toUpperCase()}? The tenant will be emailed to confirm.`)) return;
    setResultSubmittingId(tx.transaction_id);
    try {
      await axios.put(`${API}/broker/transactions/${tx.transaction_id}/result`, { result });
      toast.success(`Marked as ${result}. Tenant emailed for confirmation.`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit result");
    } finally {
      setResultSubmittingId(null);
    }
  };

  const processPayout = async () => {
    if (!payoutDialog) return;
    try {
      const res = await axios.post(`${API}/broker/transactions/${payoutDialog.transaction_id}/payout`, { paid_out_reference: paymentRef });
      toast.success(res.data?.message || "Payout processed");
      setPayoutDialog(null);
      setPaymentRef("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to process payout");
    }
  };

  const fmt = (n) => `K ${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

  const resolveDispute = async () => {
    if (!disputeDialog) return;
    setResolvingDispute(true);
    try {
      await axios.post(
        `${API}/broker/transactions/${disputeDialog.transaction_id}/resolve-dispute`,
        { outcome: disputeOutcome, reason: disputeReason.trim() || undefined }
      );
      toast.success("Dispute resolved — proceed with payout");
      setDisputeDialog(null);
      setDisputeReason("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resolve dispute");
    } finally {
      setResolvingDispute(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  const s = data?.summary || {};
  const txs = data?.transactions || [];

  return (
    <div className="space-y-6" data-testid="broker-dashboard">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
        <div>
          <h1 className="text-2xl font-bold">Broker Transactions</h1>
          <p className="text-muted-foreground text-sm">
            {isAccountsOrAdmin ? "Manage and process payouts for all broker transactions" : "Deposit tenant commissions and submit results"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
          {!isAccountsOrAdmin && (
            <Button onClick={() => setShowForm(true)} data-testid="new-deposit-btn"><Plus className="w-4 h-4 mr-2" />New Deposit</Button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={<DollarSign className="w-5 h-5" />} label="Total Deposits" value={fmt(s.total_deposits)} color="bg-blue-500" />
        <Kpi icon={<Banknote className="w-5 h-5" />} label="Paid Out" value={fmt(s.total_paid_out)} color="bg-emerald-500" />
        <Kpi icon={<Clock className="w-5 h-5" />} label="Pending Payout" value={fmt(s.pending_payout)} color="bg-amber-500" />
        {isAccountsOrAdmin && <Kpi icon={<CheckCircle2 className="w-5 h-5" />} label="Commission Earned" value={fmt(s.total_commission)} color="bg-purple-500" />}
      </div>

      {/* Table */}
      <Card>
        <CardHeader><CardTitle>Transactions ({s.total_count || 0})</CardTitle></CardHeader>
        <CardContent>
          {txs.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No transactions yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2">ID</th>
                    {isAccountsOrAdmin && <th className="text-left p-2">Broker</th>}
                    <th className="text-left p-2">Tenant</th>
                    <th className="text-right p-2">Deposit</th>
                    <th className="text-right p-2">Payout (90%)</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Tenant Confirm</th>
                    <th className="text-right p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {txs.map((tx) => {
                    const status = STATUS_META[tx.status] || { label: tx.status, color: "bg-gray-100 text-gray-700" };
                    return (
                      <tr key={tx.transaction_id} className="border-b hover:bg-gray-50" data-testid={`tx-row-${tx.transaction_id}`}>
                        <td className="p-2 font-mono text-xs">{tx.transaction_id.slice(0, 12)}...</td>
                        {isAccountsOrAdmin && <td className="p-2">{tx.broker_name}</td>}
                        <td className="p-2">
                          <div>{tx.tenant_name}</div>
                          {tx.tenant_email && <div className="text-xs text-muted-foreground">{tx.tenant_email}</div>}
                        </td>
                        <td className="p-2 text-right">{fmt(tx.deposit_amount)}</td>
                        <td className="p-2 text-right font-semibold">{fmt(tx.payout_amount)}</td>
                        <td className="p-2">
                          <span className={`text-xs px-2 py-1 rounded ${status.color}`}>{status.label}</span>
                          {tx.broker_result && (
                            <span className={`ml-1 text-xs ${tx.broker_result === "success" ? "text-emerald-600" : "text-red-600"}`}>
                              ({tx.broker_result})
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-xs">
                          {tx.tenant_confirmed ? <span className="text-emerald-600">✓ Confirmed</span> :
                           tx.tenant_disputed ? <span className="text-red-600">✕ Disputed</span> :
                           tx.broker_result ? <span className="text-amber-600">Awaiting</span> : "—"}
                        </td>
                        <td className="p-2 text-right space-x-1">
                          {!isAccountsOrAdmin && tx.status === "pending_confirmation" && (
                            <>
                              <Button size="sm" variant="outline" className="text-emerald-600 hover:bg-emerald-50" onClick={() => submitResult(tx, "success")} disabled={resultSubmittingId === tx.transaction_id} data-testid={`mark-success-${tx.transaction_id}`}>✓</Button>
                              <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => submitResult(tx, "failed")} disabled={resultSubmittingId === tx.transaction_id} data-testid={`mark-failed-${tx.transaction_id}`}>✕</Button>
                            </>
                          )}
                          {isAccountsOrAdmin && (tx.status === "confirmed_success" || tx.status === "confirmed_failed") && (
                            <Button size="sm" onClick={() => setPayoutDialog(tx)} data-testid={`payout-${tx.transaction_id}`}>
                              <Banknote className="w-3 h-3 mr-1" />Pay Out
                            </Button>
                          )}
                          {isAccountsOrAdmin && tx.tenant_disputed && tx.status === "pending_confirmation" && (
                            <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" onClick={() => { setDisputeDialog(tx); setDisputeOutcome("broker_wins"); setDisputeReason(""); }} data-testid={`resolve-dispute-${tx.transaction_id}`}>
                              Resolve
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <CardHeader><CardTitle>New Deposit</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Field label="Tenant Name *" v={formData.tenant_name} on={(v) => setFormData({ ...formData, tenant_name: v })} test="tenant-name" />
              <Field label="Tenant Email" type="email" v={formData.tenant_email} on={(v) => setFormData({ ...formData, tenant_email: v })} test="tenant-email" />
              <Field label="Tenant Phone" v={formData.tenant_phone} on={(v) => setFormData({ ...formData, tenant_phone: v })} />

              {/* Payment Method selector */}
              <div>
                <label className="block text-sm font-medium mb-1">Payout Method *</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tenant_payment_method: "bank" })}
                    className={`flex-1 px-3 py-2 border rounded text-sm font-medium transition-colors ${
                      formData.tenant_payment_method === "bank"
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                    data-testid="payment-method-bank-btn"
                  >
                    Bank Account
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tenant_payment_method: "mobile_money" })}
                    className={`flex-1 px-3 py-2 border rounded text-sm font-medium transition-colors ${
                      formData.tenant_payment_method === "mobile_money"
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                    data-testid="payment-method-mobile-btn"
                  >
                    Mobile Money
                  </button>
                </div>
              </div>

              {formData.tenant_payment_method === "bank" && (
                <>
                  <Field label="Tenant Bank *" v={formData.tenant_bank_name} on={(v) => setFormData({ ...formData, tenant_bank_name: v })} test="tenant-bank" />
                  <Field label="Tenant Account Number *" v={formData.tenant_account_number} on={(v) => setFormData({ ...formData, tenant_account_number: v })} test="tenant-account" />
                </>
              )}

              {formData.tenant_payment_method === "mobile_money" && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Mobile Provider *</label>
                    <select
                      value={formData.tenant_mobile_provider}
                      onChange={(e) => setFormData({ ...formData, tenant_mobile_provider: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                      data-testid="tenant-mobile-provider"
                    >
                      <option value="MTN">MTN Mobile Money</option>
                      <option value="Airtel">Airtel Money</option>
                      <option value="Zamtel">Zamtel Kwacha</option>
                    </select>
                  </div>
                  <Field
                    label="Mobile Number *"
                    v={formData.tenant_mobile_number}
                    on={(v) => setFormData({ ...formData, tenant_mobile_number: v })}
                    test="tenant-mobile-number"
                  />
                </>
              )}

              <Field label="Deposit Amount (ZMW) *" type="number" v={formData.deposit_amount} on={(v) => setFormData({ ...formData, deposit_amount: v })} test="deposit-amount" />
              <Field label="Property Reference" v={formData.property_reference} on={(v) => setFormData({ ...formData, property_reference: v })} />
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
              </div>
              {formData.deposit_amount && parseFloat(formData.deposit_amount) > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded text-sm">
                  <div>Commission to us (10%): <strong>K {(parseFloat(formData.deposit_amount) * 0.10).toFixed(2)}</strong></div>
                  <div>Payout to recipient (90%): <strong>K {(parseFloat(formData.deposit_amount) * 0.90).toFixed(2)}</strong></div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={submitting} data-testid="save-deposit-btn">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Record Deposit
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payout dialog */}
      {payoutDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader><CardTitle>Process Payout</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-gray-50 p-3 rounded text-sm">
                <p><strong>Transaction:</strong> {payoutDialog.transaction_id}</p>
                <p><strong>Recipient:</strong> {payoutDialog.status === "confirmed_success" ? `${payoutDialog.broker_name} (Broker)` : `${payoutDialog.tenant_name} (Tenant)`}</p>
                <p><strong>Amount to pay:</strong> <span className="text-lg font-bold">{fmt(payoutDialog.payout_amount)}</span></p>
                <p><strong>Commission retained:</strong> {fmt(payoutDialog.commission_amount)}</p>
                {payoutDialog.status === "confirmed_success" && (
                  payoutDialog.broker_payment_method === "mobile_money" ? (
                    <p className="mt-2">{payoutDialog.broker_mobile_provider} Mobile Money: {payoutDialog.broker_mobile_number || "—"}</p>
                  ) : (
                    <p className="mt-2">Bank: {payoutDialog.broker_bank_name || "—"} / {payoutDialog.broker_account_number || "—"}</p>
                  )
                )}
                {payoutDialog.status === "confirmed_failed" && (
                  payoutDialog.tenant_payment_method === "mobile_money" ? (
                    <p className="mt-2">{payoutDialog.tenant_mobile_provider} Mobile Money: {payoutDialog.tenant_mobile_number || "—"}</p>
                  ) : (
                    <p className="mt-2">Bank: {payoutDialog.tenant_bank_name || "—"} / {payoutDialog.tenant_account_number || "—"}</p>
                  )
                )}
              </div>
              <Field label="Payment Reference (optional)" v={paymentRef} on={setPaymentRef} test="payout-ref" />
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setPayoutDialog(null); setPaymentRef(""); }}>Cancel</Button>
                <Button onClick={processPayout} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-payout-btn">
                  Confirm Payout
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {disputeDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="resolve-dispute-dialog">
          <Card className="w-full max-w-md">
            <CardHeader><CardTitle>Resolve Dispute</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 p-3 rounded text-sm">
                <p><strong>Transaction:</strong> {disputeDialog.transaction_id}</p>
                <p><strong>Broker:</strong> {disputeDialog.broker_name}</p>
                <p><strong>Tenant:</strong> {disputeDialog.tenant_name}</p>
                <p><strong>Deposit:</strong> {fmt(disputeDialog.deposit_amount)}</p>
                <p><strong>Broker said:</strong> <span className={disputeDialog.broker_result === "success" ? "text-emerald-600 font-medium" : "text-red-600 font-medium"}>{(disputeDialog.broker_result || "—").toUpperCase()}</span></p>
                <p className="text-red-700 mt-1"><strong>Tenant disputed.</strong></p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Outcome</label>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                    <input type="radio" name="outcome" value="broker_wins" checked={disputeOutcome === "broker_wins"} onChange={() => setDisputeOutcome("broker_wins")} className="mt-1" data-testid="outcome-broker-wins" />
                    <div className="text-sm">
                      <div className="font-medium text-emerald-700">Side with broker</div>
                      <div className="text-muted-foreground text-xs">{fmt(disputeDialog.payout_amount)} → broker. 10% commission retained.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                    <input type="radio" name="outcome" value="tenant_wins" checked={disputeOutcome === "tenant_wins"} onChange={() => setDisputeOutcome("tenant_wins")} className="mt-1" data-testid="outcome-tenant-wins" />
                    <div className="text-sm">
                      <div className="font-medium text-red-700">Side with tenant</div>
                      <div className="text-muted-foreground text-xs">{fmt(disputeDialog.payout_amount)} refunded to tenant. 10% commission retained.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                    <input type="radio" name="outcome" value="cancel" checked={disputeOutcome === "cancel"} onChange={() => setDisputeOutcome("cancel")} className="mt-1" data-testid="outcome-cancel" />
                    <div className="text-sm">
                      <div className="font-medium text-gray-700">Cancel transaction</div>
                      <div className="text-muted-foreground text-xs">{fmt(disputeDialog.payout_amount)} refunded to tenant. <strong>10% commission still retained</strong> to prevent collusion.</div>
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Reason (optional)</label>
                <Textarea rows={2} value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} placeholder="Brief note for the audit trail" data-testid="dispute-reason-input" />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setDisputeDialog(null); setDisputeReason(""); }} disabled={resolvingDispute}>Cancel</Button>
                <Button onClick={resolveDispute} disabled={resolvingDispute} data-testid="confirm-resolve-dispute-btn">
                  {resolvingDispute ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resolving...</> : "Resolve & Continue"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const Kpi = ({ icon, label, value, color }) => (
  <Card>
    <CardContent className="pt-4">
      <div className="flex items-center gap-3">
        <div className={`${color} text-white p-2 rounded-lg`}>{icon}</div>
        <div>
          <div className="text-xl font-bold">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const Field = ({ label, v, on, type = "text", test }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <Input type={type} value={v} onChange={(e) => on(e.target.value)} data-testid={test ? `${test}-input` : undefined} />
  </div>
);

export default BrokerDashboard;
